import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { t, archetypeLabel } from "../lib/i18n";

const ARCHETYPES = [
  { id: "All", label: t.filterAll },
  { id: "Attack", label: t.archAttack },
  { id: "Defense", label: t.archDefense },
  { id: "Stamina", label: t.archStamina },
  { id: "Balanced", label: t.archBalanced },
];

const TABS = [
  { id: "decks", label: t.decksTitle },
  { id: "blade", label: t.tabBlades },
  { id: "ratchet", label: t.tabRatchets },
  { id: "bit", label: t.tabBits },
  { id: "lockchip", label: t.tabLockchips },
  { id: "assist_blade", label: t.tabAssistBlades },
  { id: "overblade", label: t.tabOverblades },
];

export default function Decks() {
  const [decks, setDecks] = useState([]);
  const [components, setComponents] = useState([]);
  const [arch, setArch] = useState("All");
  const [tab, setTab] = useState("decks");

  useEffect(() => {
    api.get("/decks").then((r) => setDecks(r.data));
    api.get("/components").then((r) => setComponents(r.data));
  }, []);

  const filtered = arch === "All" ? decks : decks.filter((d) => d.archetype === arch);
  const compType = (type) => components.filter((c) => c.type === type);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between border-b border-white/10 pb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {t.arsenal}
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl sm:text-6xl mt-2">
            {t.decksTitle}
          </h1>
        </div>
      </div>

      <div className="border-b border-white/10 flex gap-1 mt-6 overflow-x-auto">
        {TABS.map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            data-testid={`deck-tab-${tt.id}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold whitespace-nowrap transition-colors border-b-2 -mb-px ${
              tab === tt.id ? "text-[#00E5FF] border-[#00E5FF]" : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            {tt.label}
          </button>
        ))}
      </div>

      {tab === "decks" && (
        <>
          <div className="flex gap-2 mt-8 flex-wrap">
            {ARCHETYPES.map((a) => (
              <button
                key={a.id}
                onClick={() => setArch(a.id)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] border ${
                  arch === a.id
                    ? "border-[#FF0055] bg-[#FF0055]/10 text-[#FF0055]"
                    : "border-white/15 text-white/60 hover:text-white"
                }`}
                data-testid={`archetype-${a.id}`}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {filtered.map((d) => (
              <Link
                key={d.id}
                to={`/decks/${d.id}`}
                data-testid={`deck-${d.id}`}
                className="group relative border border-white/10 bg-[#0C0C14] p-6 hover:border-[#FF0055]/60 transition-colors overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#FF0055] opacity-60 group-hover:opacity-100" />
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF0055]">
                  {archetypeLabel(d.archetype)}
                </div>
                <div className="font-display font-black uppercase italic text-3xl mt-2 group-hover:text-[#00E5FF]">
                  {d.name}
                </div>
                <p className="text-xs text-white/60 mt-3 line-clamp-2">{d.description}</p>
                {(() => {
                  // Iter 41 — show every registered bey (3 for standard,
                  // 4 for BEY-PONG, 6 for Pokeblade) instead of a hardcoded
                  // 3-column preview.
                  const beys = d.beys && d.beys.length ? d.beys : (d.blades || []).map((bid) => ({ blade: bid }));
                  const cols = beys.length === 6 ? "grid-cols-3" : beys.length === 4 ? "grid-cols-4" : "grid-cols-3";
                  return (
                    <div className={`mt-5 grid ${cols} gap-2`} data-testid={`deck-${d.id}-blades`}>
                      {beys.map((b, i) => {
                        const blade = components.find((c) => c.id === (b.blade || b.over_blade));
                        return (
                          <div
                            key={i}
                            className="border border-white/10 bg-[#05050A] p-2 text-[10px] uppercase font-mono tracking-tight text-center truncate"
                          >
                            {blade?.name?.split(" ")[0] || "—"}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </Link>
            ))}
          </div>
        </>
      )}

      {tab !== "decks" && (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {compType(tab).map((c) => (
            <ComponentCard key={c.id} c={c} />
          ))}
          {compType(tab).length === 0 && (
            <div className="col-span-full text-center text-white/40 py-20 font-mono text-sm">
              {t.none}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ComponentCard({ c }) {
  return (
    <div
      data-testid={`component-${c.id}`}
      className="border border-white/10 bg-[#0C0C14] p-5 hover:border-[#00E5FF]/40 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-display font-bold uppercase italic text-xl truncate">{c.name}</div>
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/40 border border-white/10 px-1.5 py-0.5 whitespace-nowrap">
          {c.type}
        </span>
      </div>
    </div>
  );
}
